import React, { useState } from "react";
import { Paperclip, Trash } from "@phosphor-icons/react";

/**
 * The attachment area of an edit box: what is already attached (each one
 * opens, each one has a bin), what was picked in this sitting, and a zone
 * that takes dragged files. (ZEN-445)
 *
 * Only the look and the behaviour are shared. Where a file is stored, and
 * what removing one means, stay with the screen that uses this: `onRemove`
 * is told which document, and the screen asks its own question. This box
 * deletes nothing and words no confirmation, so no screen can inherit
 * another's: on a 529 distribution removal deletes a tax record, on a gift
 * it deletes nothing. A screen that lists stored documents without saying
 * what removing one does is refused outright.
 *
 * Props:
 *   existing       — [{ id, label, href }] already attached to the record
 *   onRemove       — (id) => void, called when a stored document's bin is pressed
 *   files          — File[] picked in this sitting
 *   onFilesChange  — (File[]) => void
 *   accept         — the file picker's accept string
 *   testid         — prefix for the test ids ("gift" gives gift-doc-input, …)
 */
export function DocumentsBox({ existing = [], onRemove, files, onFilesChange, accept, testid }) {
  const [dragOver, setDragOver] = useState(false);
  if (existing.length > 0 && typeof onRemove !== "function") {
    throw new Error("DocumentsBox: stored documents are listed, so the screen must say what removing one does (onRemove).");
  }
  const add = (picked) => { if (picked.length) onFilesChange([...files, ...picked]); };
  return (
    <>
      {existing.length > 0 && (
        <div className="mb-1.5 space-y-1" data-testid={`${testid}-existing-docs`}>
          {existing.map((d) => (
            <div key={d.id} className="flex items-center gap-2">
              <a href={d.href} target="_blank" rel="noreferrer"
                className="inline-flex min-w-0 max-w-full items-center gap-1.5 break-all rounded-md bg-[#22C55E]/10 px-2.5 py-1 text-xs font-semibold text-[#077A39] hover:bg-[#22C55E]/20">
                <Paperclip size={14} weight="bold" className="shrink-0" /> {d.label || "Document"}
              </a>
              <button type="button" onClick={() => onRemove(d.id)} data-testid={`${testid}-existing-remove-${d.id}`}
                className="rounded p-0.5 text-[#F01919] hover:bg-[#F01919]/10" title="Remove document"><Trash size={14} weight="bold" /></button>
            </div>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div className="mb-1.5 space-y-1" data-testid={`${testid}-doc-files`}>
          {files.map((df, i) => (
            <div key={i} className="flex items-center gap-2">
              {/* A file name has no spaces to wrap at, so it may break
                  anywhere rather than push the form wide. (ZEN-303) */}
              <span className="inline-flex min-w-0 max-w-full items-center gap-1.5 break-all rounded-md bg-[#22C55E]/10 px-2.5 py-1 text-xs font-semibold text-[#077A39]">
                <Paperclip size={14} weight="bold" className="shrink-0" /> {df.name}
              </span>
              <button type="button" onClick={() => onFilesChange(files.filter((_, j) => j !== i))}
                className="rounded p-0.5 text-[#F01919] hover:bg-[#F01919]/10" title="Remove document"><Trash size={14} weight="bold" /></button>
            </div>
          ))}
        </div>
      )}
      <label
        className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed px-3 py-3 text-sm transition-colors ${dragOver ? "border-[#034AA2] bg-[#034AA2]/5" : "border-[#C2C9D1] bg-white hover:border-[#034AA2]"}`}
        data-testid={`${testid}-doc-label`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); add(Array.from(e.dataTransfer?.files || [])); }}
      >
        <Paperclip size={18} weight="bold" className={dragOver ? "text-[#034AA2]" : "text-[#6b7280]"} />
        <span className="text-[#6b7280]">{dragOver ? "Drop to attach" : "Drop files here, or click to browse"}</span>
        <span className="text-[10px] text-[#9aa1a9]">PDF or image · multiple allowed</span>
        <input type="file" accept={accept} multiple className="hidden"
          onChange={(e) => { add(Array.from(e.target.files || [])); e.target.value = ""; }} data-testid={`${testid}-doc-input`} />
      </label>
    </>
  );
}
